import React, { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Build a full URL for both stored paths (/api/files/...) and pre-existing external URLs
export const imgUrl = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  const low = s.toLowerCase();
  if (low.startsWith("http://") || low.startsWith("https://")) return s;
  if (low.startsWith("//")) return `https:${s}`;
  if (s.startsWith("/api/")) return `${BACKEND}${s}`;
  return s;
};

export default function ImageUploader({ images = [], onChange, testid = "uploader" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    const uploaded = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    setBusy(false);
    if (uploaded.length) {
      onChange([...(images || []), ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}`);
    }
  };

  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));
  const addUrl = () => {
    const v = urlInput.trim();
    if (!v) return;
    onChange([...(images || []), v]);
    setUrlInput("");
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="grid grid-cols-4 gap-2">
        {images.map((u, i) => (
          <div key={i} className="relative aspect-[3/4] bg-[#FCE4EC] border" style={{ borderColor: "rgba(212,175,55,0.4)" }} data-testid={`${testid}-image-${i}`}>
            <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => removeAt(i)}
              className="absolute top-1 right-1 w-6 h-6 bg-white/95 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600"
              data-testid={`${testid}-remove-${i}`}>
              <X size={12} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="aspect-[3/4] border-2 border-dashed flex flex-col items-center justify-center gap-1 text-xs text-[#1E293B]/60 hover:bg-white/50 disabled:opacity-50"
          style={{ borderColor: "rgba(212,175,55,0.6)" }} data-testid={`${testid}-pick`}>
          {busy ? <Loader2 size={20} className="animate-spin text-[#D4AF37]" /> : <Upload size={20} className="text-[#D4AF37]" />}
          <span className="text-center leading-tight px-1">{busy ? "Uploading…" : "Upload"}</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(e.target.files)} data-testid={`${testid}-file-input`} />
      <div className="flex gap-2">
        <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder="Or paste an image URL"
          className="flex-1 border px-2 py-1.5 text-xs" style={{ borderColor: "rgba(212,175,55,0.35)" }} data-testid={`${testid}-url-input`} />
        <button type="button" onClick={addUrl} className="text-xs px-3 border" style={{ borderColor: "rgba(212,175,55,0.35)" }} data-testid={`${testid}-url-add`}>Add URL</button>
      </div>
    </div>
  );
}
